import { useEffect, useState } from "react";
import { publicApi } from "@/lib/api";
import { TID } from "@/lib/constants";
import { formatDistanceToNow } from "date-fns";
import { Pin } from "lucide-react";

export const GAZETTE_LAST_SEEN_KEY = "nobsgs_gazette_last_seen";

export function useGazetteUnread(latestPublishedAt) {
  const [unread, setUnread] = useState(false);
  useEffect(() => {
    try {
      const seen = localStorage.getItem(GAZETTE_LAST_SEEN_KEY);
      if (!latestPublishedAt) { setUnread(false); return; }
      if (!seen) { setUnread(true); return; }
      setUnread(new Date(latestPublishedAt).getTime() > new Date(seen).getTime());
    } catch (err) {
      console.warn("[gazette] Unread detection failed (localStorage blocked):", err?.message || err);
      setUnread(false);
    }
  }, [latestPublishedAt]);
  return unread;
}

export default function Gazette() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await publicApi.gazette();
        setPosts(d.posts || []);
        try { localStorage.setItem(GAZETTE_LAST_SEEN_KEY, new Date().toISOString()); }
        catch (err) { console.warn("[gazette] localStorage write failed:", err?.message || err); }
      } catch (err) {
        console.error("[gazette] Failed to load gazette posts:", err);
        setPosts([]);
      }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#111622]">
      <div className="mx-auto w-full max-w-[760px] px-4 sm:px-6 pt-5" style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}>
        <h1 className="text-lg font-semibold text-[#F8FAFC]">The Gazette</h1>
        <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Official notifications and app updates. No spam ever.</p>

        {loading ? (
          <div className="mt-6 space-y-3">
            {[0,1].map((k)=>(<div key={k} className="h-24 rounded-2xl bg-[#1A2232] border border-[#26334D] animate-pulse" />))}
          </div>
        ) : posts.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-[#26334D] bg-[#1A2232] p-6 text-center">
            <p className="text-sm font-semibold text-[#F8FAFC]">Board is quiet.</p>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>No new notices. That means no noise today.</p>
          </div>
        ) : (
          <ul className="mt-5 space-y-3">
            {posts.map((p) => (
              <li
                key={p.post_id}
                data-testid={TID.gazettePost}
                className={`rounded-2xl bg-[#1A2232] border p-5 shadow-[0_10px_30px_rgba(0,0,0,0.30)] ${p.is_pinned ? "border-[#F2BD68]/40 shadow-[0_0_0_1px_rgba(242,189,104,0.10),0_10px_30px_rgba(0,0,0,0.30)]" : "border-[#26334D]"}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 data-testid={TID.gazettePostTitle} className="text-sm font-semibold text-[#F8FAFC]">{p.title}</h3>
                  {p.is_pinned && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-[#F2BD68]/40 bg-[#F2BD68]/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-[#F2BD68]"
                      title="Pinned by DISTILL"
                    >
                      <Pin className="h-3 w-3" aria-hidden fill="currentColor" strokeWidth={0} />
                      Pinned
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-[#E2E8F0]" style={{ lineHeight: 1.6 }}>{p.body}</p>
                <p className="mt-3 text-xs text-[#94A3B8]">{formatDistanceToNow(new Date(p.published_at), { addSuffix: true })}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
